import { compact } from 'lodash'
import { decodeEventLog, erc20Abi, erc721Abi, Hex, parseAbiItem } from 'viem'

import { SupportedChainId } from '~/constants/chains'
import { IAddress } from '~/types'
import { EvmAddress } from '~/utils/address'
import { captureException } from '~/utils/errors'
import { unwrapToken } from '~/utils/tokens'

import type {
  ApprovalChange,
  CombinedStateChangeResult,
  CustomLog,
  NFTStateChange,
  NFTTransferEvent,
  PartitionedLogs,
  TransferEvent,
} from '~/components/txn-display/state-change/types'

export const ERC20_TRANSFER_TOPIC =
  '0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef' as Hex

const ERC20_APPROVAL_TOPIC =
  '0x8c5be1e5ebec7d5bd14f71427d1e84f3dd0314c0f7b2291e5b200ac8c7c3b925' as Hex

export const ERC1155_TRANSFER_SINGLE_TOPIC =
  '0xc3d58168c5ae7397731d063d5bbf3d657854427343f4c083240f7aacaa2d0f62' as Hex

export const ERC1155_TRANSFER_BATCH_TOPIC =
  '0x4a39dc06d4c0dbc64b70af90fd698a233a518aa5d07e595d983b8c0526c8f7fb' as Hex

export const deriveStateChangeResult = ({
  addressesToWatch,
  chainId,
  simulateCallsData,
}: {
  addressesToWatch: IAddress[]
  chainId: SupportedChainId
  simulateCallsData: CustomLog[]
}): CombinedStateChangeResult => {
  const watchSet = buildWatchAddressSet(addressesToWatch)
  const {
    erc20Transfers,
    erc721Transfers,
    erc1155SingleTransfers,
    erc1155BatchTransfers,
    approvals,
  } = partitionLogs(simulateCallsData)

  const transferEvents = compact(erc20Transfers.map(decodeErc20Transfer))
  const watchedTransferEvents = filterEventsForWatchlist(
    transferEvents,
    watchSet,
  )
  const stateChanges = buildStateChangesFromTransfers(
    watchedTransferEvents,
    watchSet,
    chainId,
  )
  const tokenOutflowTotals = buildTokenOutflowTotals(
    watchedTransferEvents,
    watchSet,
  )

  const nftEvents = [
    ...compact(erc721Transfers.map(decodeErc721Transfer)),
    ...compact(erc1155SingleTransfers.map(decodeErc1155SingleTransfer)),
    ...erc1155BatchTransfers.flatMap(decodeErc1155BatchTransfer),
  ]
  const watchedNftEvents = filterEventsForWatchlist(nftEvents, watchSet)
  const nftStateChanges = buildNFTStateChanges(
    watchedNftEvents,
    watchSet,
    chainId,
  )

  const approvalChanges = buildApprovalChanges(approvals, watchSet, chainId)

  return {
    stateChanges,
    approvalChanges,
    nftStateChanges,
    tokenOutflowTotals,
  }
}

const partitionLogs = (logs: CustomLog[] = []): PartitionedLogs => {
  const erc20Transfers = logs.filter(
    (log) => log.topics[0] === ERC20_TRANSFER_TOPIC && log.topics.length === 3,
  )

  const erc721Transfers = logs.filter(
    (log) => log.topics[0] === ERC20_TRANSFER_TOPIC && log.topics.length === 4,
  )

  const approvals = logs.filter((log) => log.topics[0] === ERC20_APPROVAL_TOPIC)

  const erc1155SingleTransfers = logs.filter(
    (log) => log.topics[0] === ERC1155_TRANSFER_SINGLE_TOPIC,
  )

  const erc1155BatchTransfers = logs.filter(
    (log) => log.topics[0] === ERC1155_TRANSFER_BATCH_TOPIC,
  )

  return {
    erc20Transfers,
    erc721Transfers,
    erc1155SingleTransfers,
    erc1155BatchTransfers,
    approvals,
  }
}

const buildWatchAddressSet = (
  addressesToWatch: IAddress[],
): ReadonlySet<IAddress> => {
  return new Set(addressesToWatch.map((address) => EvmAddress.format(address)))
}

const decodeErc20Transfer = (log: CustomLog): TransferEvent | undefined => {
  try {
    const decodedLog = decodeEventLog({
      abi: erc20Abi,
      data: log.data,
      topics: log.topics,
    })

    if (decodedLog.eventName === 'Transfer') {
      return {
        token: unwrapToken(EvmAddress.format(log.address)),
        from: EvmAddress.format(decodedLog.args.from),
        to: EvmAddress.format(decodedLog.args.to),
        amount: decodedLog.args.value,
      }
    }
  } catch (error) {
    captureException(new Error('Failed to decode transfer event'), {
      extra: { log, error },
    })
  }
}

const decodeErc721Transfer = (log: CustomLog): NFTTransferEvent | undefined => {
  try {
    const decodedLog = decodeEventLog({
      abi: erc721Abi,
      data: log.data,
      topics: log.topics,
    })

    if (decodedLog.eventName === 'Transfer') {
      return {
        token: EvmAddress.format(log.address),
        from: EvmAddress.format(decodedLog.args.from),
        to: EvmAddress.format(decodedLog.args.to),
        tokenId: decodedLog.args.tokenId,
        amount: 1n,
        tokenType: 'ERC721',
      }
    }
  } catch (error) {
    captureException(new Error('Failed to decode ERC-721 transfer event'), {
      extra: { log, error },
    })
    return undefined
  }
}

const decodeErc1155SingleTransfer = (
  log: CustomLog,
): NFTTransferEvent | undefined => {
  try {
    const decodedLog = decodeEventLog({
      abi: [
        parseAbiItem(
          'event TransferSingle(address indexed operator, address indexed from, address indexed to, uint256 id, uint256 value)',
        ),
      ],
      data: log.data,
      topics: log.topics,
    })

    if (decodedLog.eventName === 'TransferSingle') {
      return {
        token: EvmAddress.format(log.address),
        from: EvmAddress.format(decodedLog.args.from),
        to: EvmAddress.format(decodedLog.args.to),
        tokenId: decodedLog.args.id,
        amount: decodedLog.args.value,
        tokenType: 'ERC1155',
      }
    }
  } catch (error) {
    captureException(new Error('Failed to decode ERC-1155 single transfer'), {
      extra: { log, error },
    })
    return undefined
  }
}

const decodeErc1155BatchTransfer = (log: CustomLog): NFTTransferEvent[] => {
  try {
    const decodedLog = decodeEventLog({
      abi: [
        parseAbiItem(
          'event TransferBatch(address indexed operator, address indexed from, address indexed to, uint256[] ids, uint256[] values)',
        ),
      ],
      data: log.data,
      topics: log.topics,
    })

    if (decodedLog.eventName === 'TransferBatch') {
      // Validate that ids and values arrays have matching lengths
      if (decodedLog.args.ids.length !== decodedLog.args.values.length) {
        captureException(
          new Error('ERC-1155 batch transfer array length mismatch'),
          {
            extra: {
              log,
              idsLength: decodedLog.args.ids.length,
              valuesLength: decodedLog.args.values.length,
            },
          },
        )
        return []
      }

      return decodedLog.args.ids.map((id, index) => ({
        token: EvmAddress.format(log.address),
        from: EvmAddress.format(decodedLog.args.from),
        to: EvmAddress.format(decodedLog.args.to),
        tokenId: id,
        amount: decodedLog.args.values[index],
        tokenType: 'ERC1155',
      }))
    }
  } catch (error) {
    captureException(new Error('Failed to decode ERC-1155 batch transfer'), {
      extra: { log, error },
    })
    return []
  }

  return []
}

const filterEventsForWatchlist = <T extends { from: IAddress; to: IAddress }>(
  events: T[],
  watchSet: ReadonlySet<IAddress>,
) =>
  events.filter((event) => watchSet.has(event.from) || watchSet.has(event.to))

const aggregateTransferAmounts = (
  events: TransferEvent[],
  watchSet: ReadonlySet<IAddress>,
) => {
  const eventsPerAddress = events.reduce(
    (acc, event) => {
      if (watchSet.has(event.from)) {
        acc[event.from] = acc[event.from] || []
        acc[event.from].push(event)
      }

      if (watchSet.has(event.to)) {
        acc[event.to] = acc[event.to] || []
        acc[event.to].push(event)
      }

      return acc
    },
    {} as Record<IAddress, TransferEvent[]>,
  )

  return Object.entries(eventsPerAddress).reduce(
    (acc, [address, accountEvents]) => {
      const account = address as IAddress
      accountEvents.forEach((event) => {
        acc[account] = acc[account] || {}
        acc[account][event.token] = acc[account][event.token] || 0n

        if (event.from === event.to) return

        if (event.from === address) {
          acc[account][event.token] -= event.amount
        } else {
          acc[account][event.token] += event.amount
        }
      })
      return acc
    },
    {} as Record<IAddress, Record<IAddress, bigint>>,
  )
}

const buildStateChangesFromTransfers = (
  events: TransferEvent[],
  watchSet: ReadonlySet<IAddress>,
  chainId: SupportedChainId,
) => {
  const transferAmountsPerToken = aggregateTransferAmounts(events, watchSet)

  return Object.entries(transferAmountsPerToken).flatMap(
    ([account, amounts]) => {
      const accountAddress = account as IAddress
      return Object.entries(amounts).map(([token, amount]) => ({
        address: accountAddress,
        token: token as IAddress,
        chainId,
        amount,
      }))
    },
  )
}

const buildTokenOutflowTotals = (
  events: TransferEvent[],
  watchSet: ReadonlySet<IAddress>,
) =>
  events.reduce(
    (acc, event) => {
      if (!watchSet.has(event.from)) return acc
      if (event.from === event.to) return acc

      acc[event.from] = acc[event.from] || {}
      acc[event.from][event.token] =
        (acc[event.from][event.token] || 0n) + event.amount

      return acc
    },
    {} as Record<IAddress, Record<IAddress, bigint>>,
  )

const buildNFTStateChanges = (
  events: NFTTransferEvent[],
  watchSet: ReadonlySet<IAddress>,
  chainId: SupportedChainId,
) =>
  events.flatMap((event) => {
    const changes: NFTStateChange[] = []
    const from = EvmAddress.format(event.from)
    const to = EvmAddress.format(event.to)

    if (watchSet.has(from)) {
      changes.push({
        address: from,
        token: event.token,
        chainId,
        tokenId: event.tokenId,
        amount: event.amount,
        tokenType: event.tokenType,
        direction: 'outflow',
      })
    }

    if (watchSet.has(to)) {
      changes.push({
        address: to,
        token: event.token,
        chainId,
        tokenId: event.tokenId,
        amount: event.amount,
        tokenType: event.tokenType,
        direction: 'inflow',
      })
    }

    return changes
  })

const buildApprovalChanges = (
  logs: CustomLog[],
  watchSet: ReadonlySet<IAddress>,
  chainId: SupportedChainId,
) =>
  compact(
    logs.map((log) => {
      try {
        const decodedLog = decodeEventLog({
          abi: erc20Abi,
          data: log.data,
          topics: log.topics,
        })

        if (decodedLog.eventName === 'Approval') {
          return {
            token: EvmAddress.format(log.address),
            owner: EvmAddress.format(decodedLog.args.owner),
            spender: EvmAddress.format(decodedLog.args.spender),
            amount: decodedLog.args.value,
          }
        }
      } catch {
        return undefined
      }
    }),
  )
    .filter((event) => watchSet.has(event.owner))
    .map((event) => ({
      owner: event.owner,
      spender: event.spender,
      token: event.token,
      chainId,
      amount: event.amount,
    })) as ApprovalChange[]
